import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './GraphicCard.css';

function GraphicCard({
  graphic,
  onDelete,
  isLocked = false,
  lockReason = 'subscription',
  onUpgradeClick,
}) {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { graphicData, templateType, Team, template } = graphic;

  const handleLoad = () => {
    if (isLocked) {
      onUpgradeClick();
      return;
    }
    navigate(`/create-graphic?id=${graphic.id}`);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(graphic.id);
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleting(false);
    }
  };

  const renderThumbnail = () => {
    const parsedData =
      typeof graphicData === 'string' ? JSON.parse(graphicData) : graphicData;

    const teamColors = {
      primary: Team.primaryColor || Team.orgPrimaryColor || '#003366',
      secondary: Team.secondaryColor || Team.orgSecondaryColor || '#FFFFFF',
    };

    const commonProps = {
      graphicData: parsedData,
      teamColors,
      backgroundStyle: template,
      homeTeamLogo: Team.logoUrl || Team.orgLogoUrl,
      awayTeamLogoPreview: parsedData.awayTeamLogo || null,
      showWatermark: false,
      dominantImage: parsedData.dominantImage || null,
      heroFont: parsedData.heroFont || 'Bebas Neue',
      heroFontSize: parsedData.heroFontSize || null,
      accentBlockColor: parsedData.accentBlockColor || null,
      dominantImageScale: parsedData.dominantImageScale ?? null,
      dominantImageX: parsedData.dominantImageX ?? null,
      dominantImageY: parsedData.dominantImageY ?? null,
      detailTextColor: parsedData.detailTextColor || null,
    };

    // Build component name: GraphicPreview + Bold/Minimal/etc
    // Special case: highlight templates use GraphicPlayerSpotlight* naming
    let componentName;
    if (templateType === 'highlight') {
      const variation =
        template.charAt(0).toUpperCase() + template.slice(1);
      componentName = `GraphicPlayerSpotlight${variation}`;
    } else {
      const typeName =
        templateType.charAt(0).toUpperCase() + templateType.slice(1);
      const variation =
        template === 'classic'
          ? ''
          : template.charAt(0).toUpperCase() + template.slice(1);
      componentName = `Graphic${typeName}${variation}`;
    }

    // Dynamically import and render
    const TemplateComponent = require(`./${componentName}`).default;
    return <TemplateComponent {...commonProps} />;
  };

  return (
    <>
      <div
        className={`graphic-card ${isLocked ? 'locked' : ''}`}
        onClick={handleLoad}
        style={{ cursor: 'pointer' }}
      >
        <div className="graphic-card-preview">
          <div className="graphic-thumbnail-wrapper">{renderThumbnail()}</div>
        </div>

        <div className="graphic-card-body">
          <div className="graphic-timestamp">
            Saved {new Date(graphic.createdAt).toLocaleDateString()}
          </div>
        </div>

        {isLocked ? (
          <div className="lock-overlay">
            <div className="lock-content">
              <span className="lock-icon">🔒</span>
              {lockReason === 'org-admin' ? (
                <div className="lock-message">🔒 Restricted by Admin</div>
              ) : (
                <button
                  className="btn-unlock"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpgradeClick();
                  }}
                >
                  ⭐ Upgrade to Access
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="graphic-card-actions">
            <button className="btn btn-primary" onClick={handleLoad}>
              Load & Edit
            </button>
            <button
              className="btn-delete-graphic"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              disabled={deleting}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {showDeleteConfirm && !isLocked && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Graphic?</h3>
            <p>
              This graphic will be moved to <strong>Recently Deleted</strong>{' '}
              for 3 days.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Move to Trash'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GraphicCard;
